import { FC } from 'react';

import { Currency, getLocaleSetting, t } from '@onlinepayments/payments-library';

import { AmountType } from '../../enums/AmountType';
import OrderAmount from '../../interfaces/OrderAmount';
import { ofAmountType } from '../../utils/misc';

import TaxDetails from './TaxDetails';

import './PriceComponent.scss';

interface IPriceComponentProps {
  orderAmounts: OrderAmount[];
  amountLeftToPay: number;
  orderCurrency: string;
}

const PriceComponent: FC<IPriceComponentProps> = ({
  orderAmounts,
  orderCurrency,
  amountLeftToPay,
}) => {
  const {
    payment: { hideServicesCostWhenZero, hideTaxFields, showTaxDetails, excludeTax },
  } = getLocaleSetting();

  const getAmount = (type: AmountType, alwaysShowGrossAmount = false) => {
    const amount = orderAmounts.find(ofAmountType(type));

    if (!amount) {
      return undefined;
    }

    return excludeTax && !alwaysShowGrossAmount ? amount.netAmount : amount.grossAmount;
  };

  const totalAmount = getAmount(AmountType.Total);
  const totalGrossAmount = getAmount(AmountType.Total, true);
  const subTotalAmount = getAmount(AmountType.Goodsvalue);
  const providedServicesAmount = getAmount(AmountType.ProvidedServices);
  const taxAmount = orderAmounts.find(ofAmountType(AmountType.Total))?.taxAmount;
  const alreadyPaidAmount = getAmount(AmountType.AlreadyPaid, true);
  const deliveryServiceAmount = getAmount(AmountType.DeliveryServices);
  const totalExcludingTax = orderAmounts.find(ofAmountType(AmountType.Total))?.netAmount;
  const discount = getAmount(AmountType.Discount);

  const leftToPayAmount =
    amountLeftToPay > 0
      ? amountLeftToPay
      : totalGrossAmount !== undefined && alreadyPaidAmount
      ? totalGrossAmount - alreadyPaidAmount
      : totalGrossAmount;

  return (
    <>
      <ul className="PriceComponent" data-testid="PriceComponent-list">
        {!!subTotalAmount && (
          <li className="flex justify-between items-center my-c-xs">
            {t('order.subtotal_label')}
            <Currency currency={orderCurrency} amount={subTotalAmount} />
          </li>
        )}

        {deliveryServiceAmount !== undefined && (
          <li className="flex justify-between items-center my-c-xs">
            {t('order.delivery_cost_label')}
            <Currency currency={orderCurrency} amount={deliveryServiceAmount} />
          </li>
        )}

        {providedServicesAmount !== undefined &&
          !(hideServicesCostWhenZero && providedServicesAmount === 0) &&
          t('order.provided_services_cost_label') && (
            <li className="flex justify-between items-center my-c-xs">
              {t('order.provided_services_cost_label')}
              <Currency currency={orderCurrency} amount={providedServicesAmount} />
            </li>
          )}

        {!!discount && (
          <li className="flex justify-between items-center my-c-xs">
            {t('order.discount_label')}
            <Currency currency={orderCurrency} amount={discount} />
          </li>
        )}

        {!hideTaxFields && !!totalAmount && totalAmount !== amountLeftToPay && excludeTax && (
          <li className="flex justify-between items-center my-c-xs">
            {t('order.total_excluding_vat_label')}
            <Currency currency={orderCurrency} amount={totalAmount} />
          </li>
        )}

        {!hideTaxFields &&
          !!totalExcludingTax &&
          totalExcludingTax !== amountLeftToPay &&
          !excludeTax && (
            <li className="flex justify-between items-center my-c-xs">
              {t('order.total_excluding_vat_label')}
              <Currency currency={orderCurrency} amount={totalExcludingTax} />
            </li>
          )}

        {!hideTaxFields && showTaxDetails && (
          <TaxDetails orderCurrency={orderCurrency} orderAmounts={orderAmounts} />
        )}

        {!hideTaxFields && !showTaxDetails && !!taxAmount && (
          <li className="flex justify-between items-center my-c-xs">
            {t('order.vat_label')}
            <Currency currency={orderCurrency} amount={taxAmount} />
          </li>
        )}

        {!!alreadyPaidAmount && !!totalGrossAmount && (
          <li className="flex justify-between items-center my-c-xs">
            {t('order.total_including_vat')}
            <Currency currency={orderCurrency} amount={totalGrossAmount} />
          </li>
        )}

        {!!alreadyPaidAmount && (
          <li className="flex justify-between items-center my-c-xs">
            {t('payment.already_payed')}
            <Currency currency={orderCurrency} amount={-alreadyPaidAmount} />
          </li>
        )}
      </ul>

      {leftToPayAmount !== undefined && (
        <div className="PriceComponent__footer flex justify-between">
          <div>
            <div className="PriceComponent__footer-label">
              {t(alreadyPaidAmount ? 'payment.left_to_pay_header' : 'order.order_total_header')}
            </div>
            <p className="PriceComponent__footer-subtitle">{t('order.including_vat_info')}</p>
          </div>
          <div className="PriceComponent__footer-amount">
            <Currency currency={orderCurrency} amount={leftToPayAmount} />
          </div>
        </div>
      )}
    </>
  );
};

export default PriceComponent;
